'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { SessionSummary } from '@/app/app/research-assistant/types';
import { useAuth } from '@/app/auth-provider';
import { researchUiCacheKey, type SlackThreadPreference } from '@/lib/slack-agent/session-ui';

interface ThreadPage {
  sessions: SessionSummary[];
  preferences: SlackThreadPreference[];
  nextCursor: string | null;
  hasMore: boolean;
}
const EMPTY: ThreadPage = { sessions: [], preferences: [], nextCursor: null, hasMore: false };
const cache = new Map<string, ThreadPage>();
const discovery = new Map<string, { cursor: string | null; completedAt: number }>();

export function useWorkspaceSlackThreadScope(preferredWorkspaceId: string | null) {
  const { user } = useAuth();
  const uid = user?.uid || null;
  const [state, setState] = useState<{ uid: string | null; workspaces: { id: string; name: string }[]; error: string | null }>({ uid: null, workspaces: [], error: null });
  const [selection, setSelection] = useState<{ uid: string | null; id: string | null }>({ uid: null, id: null });
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!user || preferredWorkspaceId) return;
    let active = true;
    let controller: AbortController | null = null;
    const refresh = async() => {
      controller?.abort();
      const requestController = new AbortController();
      controller = requestController;
      try {
        const token = await user.getIdToken();
        const response = await fetch('/api/integrations/slack/threads/workspaces', {
          headers: { Authorization: `Bearer ${token}` }, signal: requestController.signal,
        });
        if (!response.ok) throw new Error('Unable to load Slack workspaces.');
        const body = await response.json();
        if (active && !requestController.signal.aborted) setState({ uid, workspaces: body.workspaces || [], error: null });
      } catch {
        if (active && !requestController.signal.aborted) setState({ uid, workspaces: [], error: 'Slack workspaces unavailable' });
      }
    };
    void refresh();
    const onFocus = () => { if (document.visibilityState === 'visible') void refresh(); };
    window.addEventListener('focus', onFocus);
    const timer = window.setInterval(onFocus, 30000);
    return () => { active = false; controller?.abort(); window.removeEventListener('focus', onFocus); window.clearInterval(timer); };
  }, [preferredWorkspaceId, revision, uid, user]);
  const workspaces = !preferredWorkspaceId && state.uid === uid ? state.workspaces : [];
  const selected = selection.uid === uid && workspaces.some(workspace => workspace.id === selection.id) ? selection.id : null;
  return {
    workspaceId: preferredWorkspaceId || selected || (workspaces.length === 1 ? workspaces[0].id : null),
    workspaces,
    error: !preferredWorkspaceId && state.uid === uid ? state.error : null,
    retry: () => setRevision(value => value + 1),
    selectWorkspace: (id: string) => setSelection({ uid, id }),
  };
}

export function useWorkspaceSlackThreads(workspaceId: string | null) {
  const { user } = useAuth();
  const key = researchUiCacheKey(user?.uid, workspaceId);
  const keyRef = useRef(key);
  keyRef.current = key;
  const [state, setState] = useState<{ key: string | null; data: ThreadPage }>({ key, data: key ? cache.get(key) || EMPTY : EMPTY });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sequence = useRef(0);
  const request = useRef<AbortController | null>(null);
  const data = state.key === key ? state.data : EMPTY;
  const dataRef = useRef(data);
  dataRef.current = data;

  const refresh = useCallback(async(more = false) => {
    if (!user || !workspaceId || !key) return;
    const cursor = more ? dataRef.current.nextCursor : null;
    if (more && !cursor) return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    const currentSequence = ++sequence.current;
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const [sessionResponse, preferenceResponse] = await Promise.all([
        fetch('/api/research-assistant', { method: 'POST', headers, signal: controller.signal,
          body: JSON.stringify({ action: 'list_workspace_slack_sessions', slackThreadMode: 'stored', workspaceId, limit: 50, ...(cursor ? { cursor } : {}) }) }),
        fetch(`/api/integrations/slack/threads/preferences?workspaceId=${encodeURIComponent(workspaceId)}`, { headers, signal: controller.signal }),
      ]);
      if (!sessionResponse.ok || !preferenceResponse.ok) throw new Error('Unable to load your Slack threads.');
      const [page, preferencePage] = await Promise.all([sessionResponse.json(), preferenceResponse.json()]);
      if (controller.signal.aborted || keyRef.current !== key || sequence.current !== currentSequence) return;
      const sessions = Array.isArray(page.sessions) ? page.sessions as SessionSummary[] : [];
      const merged = new Map((more ? dataRef.current.sessions : []).map(session => [session.id, session]));
      for (const session of sessions) merged.set(session.id, session);
      const next: ThreadPage = { sessions: [...merged.values()], preferences: preferencePage.preferences || [], nextCursor: page.nextCursor || null, hasMore: page.hasMore === true };
      cache.set(key, next);
      if (cache.size > 20) cache.delete(cache.keys().next().value!);
      setState({ key, data: next });
      setError(null);
    } catch (cause) {
      if (!controller.signal.aborted && keyRef.current === key && sequence.current === currentSequence) {
        cache.delete(key);
        setState({ key, data: EMPTY });
        setError(cause instanceof Error ? cause.message : 'Unable to load Slack threads.');
      }
    } finally {
      if (keyRef.current === key && sequence.current === currentSequence) setLoading(false);
    }
  }, [key, user, workspaceId]);

  useEffect(() => {
    setState({ key, data: key ? cache.get(key) || EMPTY : EMPTY });
    setError(null);
    void refresh();
    const onFocus = () => { if (document.visibilityState === 'visible') void refresh(); };
    // Discovery checks live Slack memberships. Refresh on entry/focus and
    // explicit changes, not continuously from every mounted project rail.
    window.addEventListener('focus', onFocus);
    return () => { request.current?.abort(); window.removeEventListener('focus', onFocus); };
  }, [key, refresh]);

  useEffect(() => {
    if (!user || !workspaceId || !key) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let controller: AbortController | null = null;
    let failures = 0;
    const schedule = (delay: number) => {
      if (active && timer === null) timer = setTimeout(() => { timer = null; void advance(); }, delay);
    };
    const advance = async() => {
      if (!active || keyRef.current !== key) return;
      if (document.visibilityState !== 'visible') return;
      const progress = discovery.get(key) || { cursor: null, completedAt: 0 };
      if (progress.completedAt && Date.now() - progress.completedAt < 300000) return;
      controller = new AbortController();
      try {
        const token = await user.getIdToken();
        const response = await fetch('/api/research-assistant', {
          method: 'POST', signal: controller.signal,
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'list_workspace_slack_sessions', slackThreadMode: 'discover', workspaceId, cursor: progress.cursor }),
        });
        if (!response.ok) throw new Error('Slack discovery unavailable');
        const page = await response.json();
        if (!active || controller.signal.aborted || keyRef.current !== key) return;
        const nextCursor = typeof page.nextCursor === 'string' ? page.nextCursor : null;
        if (nextCursor && nextCursor === progress.cursor) throw new Error('Slack discovery did not advance');
        discovery.set(key, { cursor: nextCursor, completedAt: nextCursor ? 0 : Date.now() });
        if (discovery.size > 20) discovery.delete(discovery.keys().next().value!);
        failures = 0;
        if (nextCursor) schedule(30000);
        else void refresh();
      } catch {
        // Discovery failure must not clear the independently authorized list.
        if (active && !controller.signal.aborted && ++failures < 3) schedule(60000);
      } finally { controller = null; }
    };
    const onFocus = () => { if (!controller) { failures = 0; schedule(5000); } };
    schedule(5000);
    window.addEventListener('focus', onFocus);
    return () => { active = false; if (timer !== null) clearTimeout(timer); controller?.abort(); window.removeEventListener('focus', onFocus); };
  }, [key, refresh, user, workspaceId]);

  const updatePreference = useCallback(async(sessionId: string, patch: { pinned?: boolean; hidden?: boolean }) => {
    if (!user || !key) return;
    const token = await user.getIdToken();
    const response = await fetch('/api/integrations/slack/threads/preferences', {
      method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, ...patch }),
    });
    if (!response.ok) throw new Error('Unable to save your thread preference.');
    if (keyRef.current !== key) return;
    await refresh();
  }, [key, refresh, user]);
  return { ...data, loading, error, refresh, loadMore: () => refresh(true), updatePreference };
}
